/**
 * web-client-app D9 — the piece contract.
 *
 * Every website section is a piece: a behavior half (`use<Piece>`: data, state,
 * rules, named actions; draws nothing) and a look half (`<Piece>View`: draws
 * the behavior half's return value, calls its actions, marks every element
 * with `data-part`). Each piece publishes, in its `contract.ts`, its options
 * (defaults and allowed values), its part names (and which a booking needs),
 * its actions and its events. `pnpm web:contract` gathers every contract into
 * `src/web/contract.json` plus the state, argument and event types into
 * `src/web/contract.d.ts`; published names only ever grow.
 *
 * Pure: no React, no DOM, so the generator and its test run in Node.
 */

export type OptionSpec =
  | {
      readonly type: 'enum';
      readonly values: readonly string[];
      readonly default: string;
      readonly description: string;
    }
  | {
      readonly type: 'boolean';
      readonly default: boolean;
      readonly description: string;
      /** The part this option shows or hides; a required part can never be hidden. */
      readonly showsPart?: string;
    }
  | {
      readonly type: 'number';
      readonly min: number;
      readonly max: number;
      readonly default: number;
      readonly description: string;
    }
  | {
      readonly type: 'string';
      readonly default: string | null;
      readonly description: string;
      readonly maxLength: number;
      /** web-studio-sites D14: a placeholder must set it, or the kit leaves the placeholder empty. */
      readonly requiredWhenPlaced?: true;
    };

/**
 * web-studio-sites D6: the shared roles a part can carry as `data-role`, so one
 * site rule reaches every main action, time, title or photo, on pieces added
 * later too. Grow-only.
 */
export const PART_ROLES = ['primary-action', 'secondary-action', 'time', 'title', 'photo'] as const;
export type PartRole = (typeof PART_ROLES)[number];

export interface PartSpec {
  readonly description: string;
  /** A part the booking needs (a price, the book action): no option or custom drawing may drop it. */
  readonly required: boolean;
  /** web-studio-sites D6: the states the part shows as `data-state` (`selected`, `disabled`, `full`). Grow-only. */
  readonly states?: readonly string[];
  /** web-studio-sites D6: the shared role the part carries as `data-role`. Once published, kept. */
  readonly role?: PartRole;
}

/**
 * web-studio-sites D7: one argument of an action, as a custom drawing sends it
 * and the kit decodes and checks it.
 */
export type ActionArgSpec =
  | {
      readonly name: string;
      /** An entry of a list in the piece's current state, read from `data-item`; an unknown or stale one does nothing. */
      readonly kind: 'item';
      /** The list, as a path into the state; `[]` steps into each entry of an array (`days[].times`). */
      readonly list: string;
      /** The entry's field the argument names; absent when the entries are the values themselves. */
      readonly key?: string;
    }
  | {
      readonly name: string;
      /** A plain value, read from `data-value` or the control's own value; out of range does nothing. */
      readonly kind: 'value';
      readonly type: 'string' | 'number' | 'boolean';
      /** What a string holds: a month (YYYY-MM), an address on this site, or free text. */
      readonly format?: 'month' | 'path' | 'text';
      /** The only values allowed. */
      readonly values?: readonly string[];
      readonly min?: number;
      readonly max?: number;
      readonly maxLength?: number;
      /** May be left out. */
      readonly optional?: true;
    }
  | {
      readonly name: string;
      /** The answers the piece's form fields collect, one per field. */
      readonly kind: 'answers';
    };

export interface ActionSpec {
  /** What the action does. */
  readonly description: string;
  /** Its arguments in order; empty for none. Their types are in `<Piece>Actions`. */
  readonly args: readonly ActionArgSpec[];
}

export interface PieceContract {
  /** Kebab-case piece name; the placeholder name custom sites use. */
  readonly name: string;
  readonly description: string;
  readonly options: Readonly<Record<string, OptionSpec>>;
  readonly parts: Readonly<Record<string, PartSpec>>;
  /** Action name → what it does and what it takes. Arguments are typed in `<Piece>Actions`. */
  readonly actions: Readonly<Record<string, ActionSpec>>;
  /** Event name → when it fires. Payloads are typed in `<Piece>Events`. */
  readonly events: Readonly<Record<string, string>>;
  /**
   * web-sign-in-checkout D8: `standard-only` for the money and consent pieces. A
   * custom site may restyle them (CSS on their parts) but never replace their
   * drawing. Absent: a custom site may draw the piece itself.
   */
  readonly drawing?: 'standard-only';
  /**
   * web-studio-sites D14: a custom page may place it with a placeholder (and,
   * unless `standard-only`, redraw it). Absent: Telify's own pages only. Once
   * placeable, always placeable.
   */
  readonly placeable?: true;
}

/** Identity helper that keeps the literal types of a contract. */
export function definePiece<const C extends PieceContract>(contract: C): C {
  return contract;
}

/** The resolved option values of a contract. */
export type ResolvedOptions<C extends PieceContract> = {
  -readonly [K in keyof C['options']]: C['options'][K] extends { type: 'boolean' }
    ? boolean
    : C['options'][K] extends { type: 'number' }
      ? number
      : C['options'][K] extends { type: 'enum'; values: readonly (infer V)[] }
        ? V
        : string | null;
};
